import { Box, Button, Flex, Heading, Stack, Text } from '@chakra-ui/react'
import React from 'react'

import type { ProgressionLevel } from '@/queries/constants'
import TutorFollowQ from '@/queries/TutorFollowQ'
import UserQ from '@/queries/UserQ'
import { useQuery } from '@/client/context/query'
import type { Query, SQLFragment } from '@/client/query'
import { getUserUrl } from '@/client/services/url'

import ShwLink from '@/components/ShwLink'
import Tabs from '@/components/Tabs/Tabs'
import ModalV3 from '@/components/UI/Modal/ModalV3'
import UserAvatar from '@/components/UserAvatar'

type ConnectionTab = 'Followers' | 'Following'

type UserConnection = {
  id: number
  name: string
  avatarUrl: string
  username: string | null
  progressionLevel: ProgressionLevel
}

const SHOW_ALL_LIMIT = 5

function ConnectionList({
  tab,
  firstName,
  users,
}: {
  tab: ConnectionTab
  firstName: string
  users: UserConnection[]
}): React.ReactElement {
  return (
    <Stack direction="column" spacing={2}>
      {users.length === 0 ? (
        <Flex justify="center" py={4}>
          {tab === 'Following' ? (
            <Box color="fg.muted">{firstName} is not following anyone yet!</Box>
          ) : (
            <Box color="fg.muted">{firstName} does not have any followers yet!</Box>
          )}
        </Flex>
      ) : null}
      {users.map(({ name, id, avatarUrl, progressionLevel, username }) => (
        <ShwLink href={getUserUrl({ userId: id, username })} key={id}>
          <Stack
            direction="row"
            spacing={4}
            align="center"
            width="100%"
            borderRadius="8px"
            _hover={{ bg: 'bg.muted' }}
            _active={{ bg: 'bg.emphasized' }}
            p={2}
          >
            <UserAvatar name={name} src={avatarUrl} size="sm" progressionLevel={progressionLevel} />
            <Text as="span" fontWeight={500} fontSize="16px" size="lg">
              {name}
            </Text>
          </Stack>
        </ShwLink>
      ))}
    </Stack>
  )
}

function ConnectionTabPanel({
  tab,
  currentTab,
  firstName,
  users,
}: {
  tab: ConnectionTab
  currentTab: ConnectionTab
  firstName: string
  users: UserConnection[]
}): React.ReactElement {
  const [showModal, setShowModal] = React.useState(false)
  return (
    <Box display={tab === currentTab ? 'auto' : 'none'} pb={4} pt={2} px={3}>
      <ConnectionList tab={tab} firstName={firstName} users={users.slice(0, SHOW_ALL_LIMIT)} />
      {users.length <= SHOW_ALL_LIMIT ? null : (
        <>
          <Box mt={2} pl={2}>
            <Button variant="ghost" onClick={() => setShowModal(true)}>
              Show More
            </Button>
          </Box>
          <ModalV3
            open={showModal}
            onClose={() => setShowModal(false)}
            title={() => <Heading size="xl">{tab}</Heading>}
            body={() => (
              <Box height="500px" overflowY="scroll">
                <ConnectionList tab={tab} firstName={firstName} users={users} />
              </Box>
            )}
          />
        </>
      )}
    </Box>
  )
}

export default function ProfileConnections({
  user,
  userIdQuery,
}: {
  user: Query
  userIdQuery: SQLFragment<number>
}): JSX.Element {
  const query = useQuery()
  const firstName = user(UserQ.first_name) ?? ''
  const isUserTutor = user(UserQ.hasTutorRole()) ?? false
  const [currentTab, setCurrenTab] = React.useState<ConnectionTab>(
    isUserTutor ? 'Followers' : 'Following'
  )
  const followingUsers: UserConnection[] = user
    .many(
      TutorFollowQ.where({
        userId: userIdQuery,
        include: [{ association: 'Tutor', required: true }],
      })
    )
    .map(connectionUser => ({
      id: connectionUser(UserQ.id) ?? 0,
      name: connectionUser(UserQ.display_name) ?? '',
      avatarUrl: connectionUser(UserQ.avatar_url) ?? '',
      username: connectionUser(UserQ.username),
      progressionLevel: connectionUser(UserQ.progression_level),
    }))

  const followerUsers: UserConnection[] = query
    .many(
      TutorFollowQ.where({
        tutorId: userIdQuery,
        include: [{ association: 'User', required: true }],
      })
    )
    .map(connectionUser => ({
      id: connectionUser(UserQ.id) ?? 0,
      name: connectionUser(UserQ.display_name) ?? '',
      avatarUrl: connectionUser(UserQ.avatar_url) ?? '',
      username: connectionUser(UserQ.username),
      progressionLevel: connectionUser(UserQ.progression_level),
    }))

  const showMoreButtonWillAppear =
    followingUsers.length > SHOW_ALL_LIMIT || followerUsers.length > SHOW_ALL_LIMIT

  return (
    <Box>
      <Heading size="xl" mb={6}>
        Connections
      </Heading>
      <Flex
        bgColor="bg"
        border="2px solid"
        borderColor="border"
        borderRadius={5}
        direction="column"
        height="100%"
      >
        <Tabs<ConnectionTab>
          currentTab={currentTab}
          tabs={isUserTutor ? ['Followers', 'Following'] : ['Following']}
          tabToLabel={{
            Followers: `Followers (${followerUsers.length})`,
            Following: `Following (${followingUsers.length})`,
          }}
          onTabChange={setCurrenTab}
          mb={2}
          pl={2}
        />
        <Box height={showMoreButtonWillAppear ? '340px' : '300px'}>
          <ConnectionTabPanel
            firstName={firstName}
            tab="Followers"
            users={followerUsers}
            currentTab={currentTab}
          />
          <ConnectionTabPanel
            firstName={firstName}
            tab="Following"
            users={followingUsers}
            currentTab={currentTab}
          />
        </Box>
      </Flex>
    </Box>
  )
}
